import { useEffect, useState } from "react";

/**
 * Sprint 24 Wave D — PR-D1: viewport-width hook for the Admin
 * Console mobile drawer breakpoint.
 *
 * Mirrors `apps/web/src/layout/useIsMobile.ts` (Sprint 16 Wave F):
 * returns `true` when the viewport is narrower than 768 px (the
 * operator-chosen mobile breakpoint, matching Tailwind's `md`).
 * Updates reactively via `MediaQueryList.change`.
 *
 * SSR-safe: returns `false` during the first render in environments
 * without `window` (jsdom in non-component-test mode); the first
 * browser-side commit resolves it via the `useEffect`.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

export const MOBILE_QUERY = "(max-width: 767px)";
